import { useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { Modal } from '@/components/settings/Modal'
import { releasesApi, type Release } from '@/lib/releasesApi'
import { useAuthStore } from '@/store/auth'
import { useVoiceStore } from '@/store/voice'
import { ReleaseBody, ReleaseCard } from './ReleaseView'
import { pendingReleaseKey, usePendingRelease } from '@/lib/pendingRelease'
import { GAME_SERVERS_RELEASE_ID } from '@/lib/gameServersAnnouncement'

/*
 * Карточка «Что нового» в доке над панелью пользователя. Ничего не перекрывает, не звучит
 * и не шлёт уведомлений; в голосе и на стриме не показывается. Открыл выпуск или нажал
 * «Скрыть» в меню — карточка пропадает на всех устройствах: сервер помнит, кто видел. Новый выпуск
 * заменяет старую карточку. Проверяем, когда окно снова в фокусе, не чаще раза в пять минут:
 * закрытая на телефоне карточка пропадёт и на компьютере, как только к нему вернутся.
 */
export function WhatsNew() {
  const userId = useAuthStore((s) => s.user?.id)
  const inVoice = useVoiceStore((s) => s.connection !== null)
  const qc = useQueryClient()
  const pending = usePendingRelease()
  const [open, setOpen] = useState<Release | null>(null)
  const candidate = pending.data?.release ?? null
  const release = candidate?.id === GAME_SERVERS_RELEASE_ID ? null : candidate

  const hide = () => qc.setQueryData(pendingReleaseKey(userId), { release: null })
  const dismiss = () => {
    if (!release) return
    hide()
    void releasesApi.seen(release.id, false).catch(() => undefined)
  }
  // открыл — значит видел: карточка уйдёт, как только окно закроют любым способом
  const show = () => {
    if (!release) return
    setOpen(release)
    void releasesApi.seen(release.id, true).catch(() => undefined)
  }
  const close = () => {
    setOpen(null)
    hide()
  }

  return (
    <>
      {release && !open && !inVoice ? <ReleaseCard title={release.title} onOpen={show} onDismiss={dismiss} /> : null}
      {open ? <ReleaseModal release={open} onClose={close} /> : null}
    </>
  )
}

export function ReleaseModal({ release, onClose }: { release: Release; onClose: () => void }) {
  return (
    <Modal title="Что нового" onClose={onClose} width={520}>
      <ReleaseBody title={release.title} items={release.items} publishedAt={release.published_at} onDone={onClose} />
    </Modal>
  )
}
